<template>
    <div class="flex flex-col gap-5">
        <div v-if="projects.length === 0" class="rounded-xl bg-stone-50 px-5 py-8 text-center text-sm text-black/60">
            {{ $t('pages.product_detail_page.product_info.product_button.recent_projects_modal.empty_state') }}
        </div>

        <div v-if="paginatedProjects.length > 0" class="space-y-3">
            <PhotobookRecentProjectsItem
                v-for="project in paginatedProjects"
                :key="project.mediaclip_project_id"
                :project="project"
                :is-opening="openingProjectId === project.mediaclip_project_id"
                @open="openProject(project)"
            />
        </div>

        <PhotobookRecentProjectsPagination
            v-if="totalPages > 1"
            :current-page="currentPage"
            :total-pages="totalPages"
            @previous="goToPreviousPage"
            @next="goToNextPage"
        />

        <p v-if="generalError" class="text-center text-sm font-medium text-red-600">
            {{ $t('pages.product_detail_page.product_info.product_button.recent_projects_modal.open_error') }}
        </p>

        <Button
            task="startOrdering"
            :label="$t('pages.product_detail_page.product_info.product_button.recent_projects_modal.start_new_project')"
            no-icon
            @click="emits('start-new')"
        />
    </div>
</template>

<script setup lang="ts">
import Button from '@/components/buttons/Button.vue';
import { useProductStore } from '@/stores/products/ProductStore';
import { useMediaclipSessionStore } from '@/stores/tool/MediaclipSessionStore';
import type { RecentProject } from '@/types/PhotobookRecentProject';
import ProductPageOpenRecentProjectController from '@/types/wayfinder/actions/App/Http/Controllers/Web/ProductPage/ProductPageOpenRecentProjectController';
import { useHttp } from '@inertiajs/vue3';
import { computed, ref } from 'vue';
import PhotobookRecentProjectsItem from './PhotobookRecentProjectsItem.vue';
import PhotobookRecentProjectsPagination from './PhotobookRecentProjectsPagination.vue';

type OpenRecentProjectResponse = {
    mediaclip_project_id: string;
    product_sku: string;
    url: string;
};

const props = defineProps<{
    projects: RecentProject[];
}>();

const emits = defineEmits<{
    'start-new': [];
    'project-opened': [url: string];
}>();

const http = useHttp<Record<string, never>, OpenRecentProjectResponse>();
const productStore = useProductStore();
const sessionStore = useMediaclipSessionStore();

const openingProjectId = ref<string | null>(null);
const generalError = ref(false);
const currentPage = ref(1);
const pageSize = 4;

const totalPages = computed(() => {
    const pages = Math.ceil(props.projects.length / pageSize);

    if (pages > 0) {
        return pages;
    }

    return 1;
});

const paginatedProjects = computed(() => {
    const startIndex = (currentPage.value - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return props.projects.slice(startIndex, endIndex);
});

async function openProject(project: RecentProject): Promise<void> {
    if (http.processing) {
        return;
    }

    openingProjectId.value = project.mediaclip_project_id;
    generalError.value = false;

    try {
        await http.post(
            ProductPageOpenRecentProjectController({
                configurableProduct: productStore.configurableProduct.url_key,
                mediaclipProjectId: project.mediaclip_project_id,
            }).url,
            {
                onSuccess: data => {
                    if (!data.url || !data.mediaclip_project_id || !data.product_sku) {
                        generalError.value = true;
                        return;
                    }

                    sessionStore.activate(data.mediaclip_project_id, data.product_sku);
                    emits('project-opened', data.url);
                },
                onHttpException: () => {
                    generalError.value = true;
                },
                onNetworkError: () => {
                    generalError.value = true;
                },
            },
        );
    } catch {
        // Errors are handled by the callbacks above
    } finally {
        openingProjectId.value = null;
    }
}

function goToPreviousPage(): void {
    if (currentPage.value === 1) {
        return;
    }

    currentPage.value -= 1;
}

function goToNextPage(): void {
    if (currentPage.value === totalPages.value) {
        return;
    }

    currentPage.value += 1;
}
</script>
